"use client";
import { jsx as _jsx } from "react/jsx-runtime";
// @owlcat/app-shell/analytics — the fleet's SINGLE PostHog product-analytics
// integration. One provider, wired once into every app's client root, so all
// owlcat apps capture the same way and split cleanly per app/domain.
//
// ── Consent ────────────────────────────────────────────────────────────────
// Gated on the fleet-wide `analytics` category (the SAME parent-domain
// `owlcat-consent` cookie the shared <CookieConsent> banner writes — see
// consent.ts). PostHog is NEVER initialised, and NOTHING is captured, until the
// user grants analytics consent. On revoke we opt out + reset; on (re-)grant we
// opt back in. The provider subscribes to `onConsentChange`, so a choice made in
// the banner (or in a sibling app, on the next cross-app hop) takes effect live.
//
// ── What it captures ─────────────────────────────────────────────────────────
//   • autocapture  — clicks + rageclicks (autocapture: true)
//   • heatmaps     — enable_heatmaps: true (PostHog toolbar heatmaps)
//   • pageviews    — capture_pageview: true (SPA route changes included)
//   • pageleave    — capture_pageleave: true (bounce/most-recent-page)
//   • person_profiles: 'identified_only' — anonymous events stay profile-less
//     (privacy + cost); a profile is created only once we identify a user.
//
// ── Per-app / domain separation ──────────────────────────────────────────────
// Each app ships its OWN PostHog PROJECT token via NEXT_PUBLIC_POSTHOG_KEY and a
// SHARED host via NEXT_PUBLIC_POSTHOG_HOST, so every app is its own PostHog
// project (clean per-project dashboards, per the owner's "analytics per project"
// ask). Belt-and-suspenders for anyone who consolidates into one project: every
// event also carries an `app_id` super-property and PostHog's own auto `$host`,
// so a single project is still fully splittable by app/domain.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, } from "react";
import posthog from "posthog-js";
import { hasConsent, onConsentChange } from "./consent";
const DEFAULT_HOST = "https://us.i.posthog.com";
/**
 * PURE: the exact PostHog init options for the fleet. Exported for testing so the
 * autocapture/heatmaps/pageview privacy contract is asserted directly.
 */
export function posthogInitOptions(host, debug) {
    return {
        api_host: host || DEFAULT_HOST,
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
        enable_heatmaps: true,
        // Disable PostHog's bundled web-vitals collector — it throws
        // "Cannot read properties of undefined (reading 'startTime')" in
        // reportAllChanges when a browser/frame hands it an undefined perf entry.
        // Web vitals are non-essential; the rest of capture_performance stays on.
        capture_performance: { web_vitals: false },
        person_profiles: "identified_only",
        persistence: "localStorage+cookie",
        debug: debug === true,
    };
}
/**
 * React-free analytics controller over the posthog-js singleton. All consent
 * gating funnels through here, so it is unit-testable without a DOM: mock
 * `posthog-js` + `./consent.hasConsent` and drive `sync`/`capture`/`identify`.
 */
export function createAnalyticsController(opts) {
    const { appId, apiKey, host, debug } = opts;
    let initialized = false;
    let identified = null;
    const sync = () => {
        // Dev/preview: NEVER send anything to PostHog (no init, no capture), even if
        // a NEXT_PUBLIC_POSTHOG_KEY is present in the dev env. Analytics is a
        // production-only concern; local/dev traffic must not pollute the project.
        // With init skipped, `initialized` stays false so capture/identify/enabled
        // are all no-ops downstream. Gated on 'development' only (the `next dev`
        // server) so the vitest suite (NODE_ENV='test') still exercises init. The
        // literal `process.env.NODE_ENV` is what the app bundler statically replaces
        // (see the module-local `declare const process` above — keeps this shared
        // package free of @types/node while the emitted dist stays replaceable).
        if (process.env.NODE_ENV === "development")
            return;
        if (!apiKey)
            return; // no key → safe no-op (dev without PostHog configured)
        if (!hasConsent("analytics")) {
            // Consent absent or revoked: stop capturing + clear stored identity.
            if (initialized) {
                posthog.opt_out_capturing();
                posthog.reset();
                initialized = false;
                identified = null;
            }
            return;
        }
        if (!initialized) {
            posthog.init(apiKey, posthogInitOptions(host, debug));
            initialized = true;
            // Split analytics by app/domain even inside a single project.
            posthog.register({ app_id: appId });
            posthog.opt_in_capturing();
        }
        else {
            // Re-granted after an earlier opt-out in this tab.
            posthog.opt_in_capturing();
        }
    };
    return {
        sync,
        enabled: () => initialized && hasConsent("analytics"),
        capture: (event, properties) => {
            if (!initialized || !hasConsent("analytics"))
                return;
            posthog.capture(event, properties);
        },
        identify: (id, properties) => {
            if (!initialized || !hasConsent("analytics"))
                return;
            if (!properties && identified === id)
                return;
            posthog.identify(id, properties);
            identified = id;
        },
        reset: () => {
            if (!initialized)
                return;
            posthog.reset();
            identified = null;
        },
    };
}
const noop = () => { };
const AnalyticsContext = createContext({
    enabled: false,
    capture: noop,
    identify: noop,
    reset: noop,
});
/**
 * Fleet-wide PostHog provider. Mount ONCE at each app's client root. Reads the
 * shared consent cookie directly (no consent provider needed) and is fully
 * SSR-safe: all PostHog access happens in effects via the controller, guarded by
 * the analytics consent category and the presence of an apiKey.
 */
export function AnalyticsProvider({ appId, apiKey, host, identityId, selfProfileUrl, debug, children, }) {
    const controllerRef = useRef(null);
    if (controllerRef.current === null) {
        controllerRef.current = createAnalyticsController({ appId, apiKey, host, debug });
    }
    const controller = controllerRef.current;
    // ── init / opt-in / opt-out driven purely by the analytics consent state ──
    useEffect(() => {
        controller.sync();
        const unsubscribe = onConsentChange(() => {
            controller.sync();
            if (identityId)
                controller.identify(identityId);
        });
        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // ── identity: explicit prop wins; else best-effort /me on consent ──
    useEffect(() => {
        if (identityId) {
            controller.identify(identityId);
            return;
        }
        if (!selfProfileUrl)
            return;
        let alive = true;
        const resolve = () => {
            if (!controller.enabled())
                return;
            void fetch(selfProfileUrl, {
                credentials: "include",
                headers: { Accept: "application/json" },
            })
                .then((r) => (r.ok ? r.json() : null))
                .then((p) => {
                if (!alive || !p?.identityId)
                    return;
                controller.identify(p.identityId);
            })
                .catch(() => { });
        };
        resolve();
        const unsubscribe = onConsentChange(() => resolve());
        return () => {
            alive = false;
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identityId, selfProfileUrl]);
    const value = useMemo(() => ({
        get enabled() {
            return controller.enabled();
        },
        capture: (event, properties) => controller.capture(event, properties),
        identify: (id, properties) => controller.identify(id, properties),
        reset: () => controller.reset(),
    }), [controller]);
    return _jsx(AnalyticsContext.Provider, { value: value, children: children });
}
/**
 * Access the analytics API from any client component under <AnalyticsProvider>.
 *   const { capture } = useAnalytics();
 *   capture("story_created", { storyId });
 * All calls are consent-gated no-ops until analytics consent is granted.
 */
export function useAnalytics() {
    return useContext(AnalyticsContext);
}
/**
 * Typed capture over the fleet taxonomy (analytics-events.ts): `track('story_created', {…})`.
 * A name outside FleetEvents or a wrong property shape is a compile error. Same consent /
 * prod-only gating as `capture`.
 */
export function useTrack() {
    const { capture } = useAnalytics();
    return useCallback((event, properties) => capture(event, properties), [capture]);
}
